<script setup>
import { getFooter } from '/api/footer.js'

const props = defineProps({
  theme: {
    required: false,
    default: 2
  }
})

const isScrolled = ref(false)
const footerData = ref([])
let scrollRaf = null

const emptyJson = () => ({
  columns: [],
  online_service: []
})

const safeJsonParse = (content) => {
  if (!content || typeof content !== 'string') return emptyJson()

  try {
    const result = JSON.parse(content.trim())
    return Array.isArray(result) && result[0] ? result[0] : emptyJson()
  } catch (error) {
    console.error('JSON解析失败:', error)
    return emptyJson()
  }
}

const isAllDayOnline = (onlineTime) => {
  if (!onlineTime) return false

  const text = String(onlineTime).trim()

  return [
    '24小时',
    '全天',
    '全天在线',
    '00:00 - 23:59',
    '00:00-23:59',
    '00:00 - 00:00',
    '00:00-00:00'
  ].includes(text)
}

const timeToMinutes = (time) => {
  if (!time || typeof time !== 'string') return null

  const [hour, minute] = time.split(':').map(Number)

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null

  return hour * 60 + minute
}

const parseTimeRange = (range) => {
  if (!range || typeof range !== 'string') return null

  const [start, end] = range.split(' - ')

  if (!start || !end) return null

  const startMinutes = timeToMinutes(start)
  const endMinutes = timeToMinutes(end)

  if (startMinutes === null || endMinutes === null) return null

  return {
    start,
    end,
    startMinutes,
    endMinutes
  }
}

const stateChange = (onlineTime, restTime) => {
  if (isAllDayOnline(onlineTime)) {
    return {
      className: 'Heziyun-online',
      text: '在线中',
      displayText: '在线中'
    }
  }

  const offline = {
    className: 'Heziyun-offline',
    text: '已离线',
    displayText: '已离线'
  }

  const onlineRange = parseTimeRange(onlineTime)
  const restRange = parseTimeRange(restTime)

  if (!onlineRange) return offline

  const now = new Date()
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes()

  const isOnline =
    currentTimeInMinutes >= onlineRange.startMinutes &&
    currentTimeInMinutes <= onlineRange.endMinutes

  if (isOnline) {
    const isRest =
      restRange &&
      currentTimeInMinutes >= restRange.startMinutes &&
      currentTimeInMinutes <= restRange.endMinutes

    if (isRest) {
      return {
        className: 'Heziyun-rest',
        text: `休息中 ${restTime}`,
        displayText: `休息中 ${restTime}`
      }
    }

    return {
      className: 'Heziyun-online',
      text: '在线中',
      displayText: '在线中'
    }
  }

  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  return {
    className: 'Heziyun-offline',
    text: `已离线 上班时间:${onlineRange.start}`,
    displayText: `已离线 上班时间:${tomorrow.getMonth() + 1}月${tomorrow.getDate()}日 ${onlineRange.start}`
  }
}

const normalizeFooterItem = (item) => {
  const jsonData = safeJsonParse(item.html)

  return {
    ...item,
    jsonData,
    columns: jsonData.columns || [],
    online_service: (jsonData.online_service || []).map(service => ({
      ...service,
      state: stateChange(service.online_time_number, service.rest_time)
    }))
  }
}

const getSideContact = async () => {
  try {
    const res = await getFooter()
    const list = res?.data?.list || []

    footerData.value = Array.isArray(list) ? list.map(normalizeFooterItem) : []
  } catch (error) {
    console.error('Error fetching footer data:', error)
    footerData.value = []
  }
}

const jumpTo = () => {
  const defaultUrl = '/home.htm'
  const frontMenusStr = localStorage.getItem('frontMenus')

  if (!frontMenusStr) {
    window.open(defaultUrl, '_blank')
    return
  }

  try {
    const frontMenus = JSON.parse(frontMenusStr)
    const ticketMenu = Array.isArray(frontMenus)
      ? frontMenus.find(menu => menu?.url?.includes('ticket.htm'))
      : null

    window.open(ticketMenu?.url || defaultUrl, '_blank')
  } catch (error) {
    console.error('解析 frontMenus 数据失败:', error)
    window.open(defaultUrl, '_blank')
  }
}

const addressRedirection = (item) => {
  if (String(item?.switch) === '1' && item.url) {
    window.open(item.url, '_blank')
  }
}

const checkScrollPosition = () => {
  const scrollTop =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0

  isScrolled.value = scrollTop > 0
}

const handleScroll = () => {
  if (scrollRaf) return

  scrollRaf = requestAnimationFrame(() => {
    checkScrollPosition()
    scrollRaf = null
  })
}

const backTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const hasFooterData = computed(() => footerData.value.length > 0)

onMounted(() => {
  getSideContact()
  checkScrollPosition()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)

  if (scrollRaf) {
    cancelAnimationFrame(scrollRaf)
    scrollRaf = null
  }
})
</script>

<template>
  <div class="Heziyun-contact" v-if="hasFooterData">
    <div class="Heziyun-rightcontent Heziyun-rightcontent3" v-if="theme == 1">
      <div class="Heziyun-contact-item">
        <a href="/cart/goodsList.htm" class="Heziyun-contact-text">
          <div class="Heziyun-contact-title"></div>
        </a>
      </div>
    </div>

    <div class="Heziyun-through" v-else>
      <div class="Heziyun-contact-item">
        <a href="/html.htm" @click.prevent="jumpTo">
          <img src="@/static/img/footer/22.gif" alt="">
          <span>提交工单</span>
        </a>
      </div>
    </div>

    <div class="Heziyun-rightcontent">
      <div
        class="Heziyun-contact-item"
        v-for="(item, index) in footerData"
        :key="item.id || index"
        @click="addressRedirection(item)"
        v-show="item"
      >
        <div class="Heziyun-contact-text">
          <div
            class="Heziyun-contact-title"
            :style="{
              background: `url(${item.small_img || ''}) no-repeat center`,
              backgroundSize: 'cover'
            }"
          ></div>

          <div class="Heziyun-contact-content Heziyun-contact-content2" v-if="String(item.switch) !== '1'">
            <img :src="item.img" v-if="item.img" alt="" loading="lazy">

            <div class="Heziyun-JsonHtml">
              <template v-if="item.columns.length > 0">
                <a
                  v-for="column in item.columns"
                  :key="column.link || column.contact_number"
                  :href="column.link"
                  :target="item.is_new_page == 1 ? '_blank' : '_self'"
                  class="contact-link"
                >
                  <a-image class="Heziyun-logo" :src="column.img_icon" :preview="false" />
                  <p class="Heziyun-textOverflow">{{ column.contact_number }}</p>
                </a>
              </template>

              <template v-if="item.online_service.length > 0">
                <div
                  v-for="(service, serviceIndex) in item.online_service"
                  :key="serviceIndex"
                  class="Heziyun-service-item"
                >
                  <a-tooltip
                    position="left"
                    trigger="hover"
                    popup-container="Heziyun-JsonHtml"
                    :content="service.online_time"
                  >
                    <a
                      :href="service.link"
                      :target="item.is_new_page == 1 ? '_blank' : '_self'"
                      class="Heziyun-itemcustomerService"
                    >
                      <div class="Heziyun-itemcustomerService-img">
                        <a-image
                          class="Heziyun-icon"
                          fit="contain"
                          :class="service.state.className"
                          :src="service.img_headPortrait"
                          :preview="false"
                        />
                        <div class="Heziyun-states" :class="service.state.className"></div>
                      </div>

                      <div class="Heziyun-itemCustomer Heziyun-textOverflow">
                        <span class="Heziyun-textOverflow">{{ service.customer_name }}</span>
                        <div class="Heziyun-onlineStatus" :class="service.state.className">
                          {{ service.state.text }}
                        </div>
                      </div>
                    </a>
                  </a-tooltip>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="Heziyun-rightcontent Heziyun-rightcontent2"
      :class="{ 'scroll-active': isScrolled }"
    >
      <div class="Heziyun-contact-item">
        <a href="javascript:;" class="Heziyun-contact-text" @click.prevent="backTop">
          <div class="Heziyun-contact-title"></div>
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.Heziyun-contact {
  animation: slideInRight 0.5s ease 1;
  position: fixed;
  right: 0.5rem;
  bottom: 10rem;
  z-index: 999;

  @keyframes slideInRight {
    0% {
      opacity: 0;
      transform: translateX(100%);
    }

    50% {
      opacity: 0;
    }

    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .Heziyun-rightcontent {
    padding: 0.1rem;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;

    .Heziyun-contact-item {
      cursor: pointer;
      border-radius: 50%;
      position: relative;
      transition: all 0.3s ease;
      background-image: linear-gradient(0deg, #ffffff 0%, #f3f5f8 100%);
      border: 2px solid #fff;
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);

      &:hover {
        background: linear-gradient(to bottom, rgb(12, 88, 252), rgb(71, 143, 255));
        box-shadow: 0 12px 40px rgba(11, 83, 255, 0.24);

        .Heziyun-contact-title {
          filter: brightness(4.2);
        }
      }

      .Heziyun-contact-text {
        width: 2.5rem;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        &:hover {
          .Heziyun-contact-content {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
        }

        .Heziyun-contact-title {
          width: 1.6rem;
          height: 1.6rem;
          transition: all 0.3s ease;
          border-radius: 50%;
        }

        .Heziyun-contact-content {
          @keyframes slideInLeft {
            0% {
              opacity: 0;
              left: -300%;
            }

            100% {
              opacity: 1;
              left: -350%;
            }
          }

          animation: slideInLeft 0.3s ease;
          width: 13rem;
          position: absolute;
          top: 50%;
          left: -350%;
          transform: translate(-50%, -50%);
          display: none;
          padding: 0.5rem;
          background-color: #fff;
          border-radius: 4px;
          box-shadow: rgba(127, 135, 144, 0.16) 0 8px 24px 0;
          z-index: 1000;

          &::after {
            content: "";
            position: absolute;
            top: 50%;
            right: -0.4rem;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-top: 0.5rem solid transparent;
            border-bottom: 0.5rem solid transparent;
            border-left: 0.5rem solid #fff;
          }

          &::before {
            content: "";
            position: absolute;
            right: -12%;
            top: 0;
            width: 100%;
            height: 100%;
          }

          a {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            padding: 0.5rem;
            width: 100%;
            gap: 0.2rem;

            &:not(:last-child) {
              border-bottom: 1px solid rgba(118, 1, 211, 0.04);
            }

            &:hover {
              background-color: #f6f6f6;

              span,
              p {
                color: #0052d9;
              }
            }
          }

          .Heziyun-logo {
            width: 1.5rem;
            height: 1.5rem;
            object-fit: contain;

            :deep(.arco-image-img) {
              width: 1.5rem;
              height: 1.5rem;
              object-fit: contain;
            }
          }

          .Heziyun-JsonHtml {
            display: flex;
            flex-direction: column;
            position: relative;

            .Heziyun-itemcustomerService {
              display: flex;
              align-items: center;
              gap: 0.5rem;

              .Heziyun-itemcustomerService-img {
                position: relative;

                &::before {
                  content: "";
                  position: absolute;
                  bottom: 0;
                  right: 0;
                  width: 0.65rem;
                  height: 0.65rem;
                  background-color: #fff;
                  z-index: 1;
                  border-radius: 50%;
                }

                .Heziyun-icon {
                  width: 2rem;
                  height: 2rem;
                  border-radius: 50%;
                  position: relative;
                  object-fit: contain;

                  :deep(.arco-image-img) {
                    width: 2rem;
                    height: 2rem;
                    border-radius: 50%;
                  }

                  &.Heziyun-offline {
                    filter: grayscale(1);
                  }
                }

                @keyframes pulse {
                  0% {
                    transform: scale(1);
                    opacity: 0.7;
                  }

                  70%,
                  100% {
                    transform: scale(2);
                    opacity: 0;
                  }
                }

                .Heziyun-states {
                  width: 0.4rem;
                  height: 0.4rem;
                  border-radius: 50%;
                  position: absolute;
                  bottom: 6%;
                  right: 6%;
                  z-index: 11;

                  &::before {
                    content: '';
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                  }

                  &.Heziyun-online {
                    background-color: #4CAF50;

                    &::before {
                      background-color: #4CAF50;
                    }
                  }

                  &.Heziyun-rest {
                    background-color: #FFC107;

                    &::before {
                      background-color: #FFC107;
                    }
                  }

                  &.Heziyun-offline {
                    background-color: #9E9E9E;

                    &::before {
                      background-color: #9E9E9E;
                    }
                  }
                }
              }

              .Heziyun-itemCustomer {
                display: flex;
                flex-direction: column;
                gap: 0.1rem;

                span {
                  font-weight: 600;
                }

                .Heziyun-onlineStatus {
                  font-size: 13px;
                  display: flex;
                  align-items: center;
                  gap: 0.2rem;

                  &.Heziyun-online {
                    color: #4CAF50;
                  }

                  &.Heziyun-rest {
                    color: #FFC107;
                  }

                  &.Heziyun-offline {
                    color: #9E9E9E;
                  }
                }
              }
            }
          }
        }

        .Heziyun-contact-content2 {
          width: 15rem;
        }
      }
    }
  }

  .Heziyun-rightcontent2 {
    margin-top: 0.4rem;
    transform: translateX(calc(100% + 0.5rem));
    transition: transform 0.2s ease;

    &.scroll-active {
      transform: translateX(0);
    }

    .Heziyun-contact-title {
      width: 1.3rem !important;
      height: 1.3rem !important;
      background: url('@/static/img/footer/shang.svg') no-repeat center;
      background-size: cover;
    }
  }

  .Heziyun-rightcontent3 {
    margin-bottom: 0.4rem;

    .Heziyun-contact-item {
      background: linear-gradient(to bottom, rgb(12, 88, 252), rgb(71, 143, 255));

      .Heziyun-contact-title {
        width: 1.4rem !important;
        height: 1.4rem !important;
        background-size: cover;
      }
    }
  }

  .Heziyun-through {
    position: relative;
    margin-bottom: 0.4rem;

    .Heziyun-contact-item {
      a {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        align-items: center;

        &:hover span {
          box-shadow: 8px 8px 20px rgba(55, 99, 170, 0.3);
        }

        span {
          writing-mode: vertical-rl;
          text-align: center;
          font-size: 0.85rem;
          letter-spacing: 4px;
          border: 2px solid #fff;
          border-radius: 10rem;
          display: flex;
          align-items: center;
          background: linear-gradient(to bottom, rgb(12, 88, 252), rgb(71, 143, 255));
          padding: 0.8rem 0.5rem;
          color: #fff;
          position: relative;
          box-shadow: 0 0 8px rgb(0 0 0 / 20%);
        }

        img {
          width: 3rem;
          height: 3rem;
          object-fit: contain;
          position: absolute;
          top: -3.5rem;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .Heziyun-contact {
    right: 1vw;
    bottom: 20%;

    .Heziyun-rightcontent {
      padding: 0.5vw;
      gap: 1vw;

      .Heziyun-contact-item:not(:last-child)::after {
        bottom: -0.5vw;
      }

      .Heziyun-contact-item {
        .Heziyun-contact-text {
          width: 10vw;
          height: 10vw;

          &:hover .Heziyun-contact-content {
            gap: 2vw;
          }

          .Heziyun-contact-title {
            width: 6vw;
            height: 6vw;
          }

          .Heziyun-contact-content {
            width: 45vw;
            padding: 2vw;
            border-radius: 1vw;

            &::after {
              right: -1vw;
              border-top: 1vw solid transparent;
              border-bottom: 1vw solid transparent;
              border-left: 1vw solid #fff;
            }

            a {
              padding: 2vw;
            }

            .Heziyun-logo {
              width: 6vw;
              height: 6vw;

              :deep(.arco-image-img) {
                width: 6vw;
                height: 6vw;
              }
            }

            .Heziyun-JsonHtml {
              .Heziyun-itemcustomerService {
                gap: 2vw;

                .Heziyun-itemcustomerService-img {
                  &::before {
                    width: 3vw;
                    height: 3vw;
                  }

                  .Heziyun-icon {
                    width: 8vw;
                    height: 8vw;
                    border-radius: 2vw;

                    :deep(.arco-image-img) {
                      width: 8vw;
                      height: 8vw;
                      border-radius: 2vw;
                    }
                  }

                  .Heziyun-states {
                    width: 2vw;
                    height: 2vw;
                    bottom: 0.5vw;
                    right: 0.5vw;
                  }
                }

                .Heziyun-itemCustomer {
                  gap: 1vw;

                  span {
                    font-size: 4vw;
                    width: 38vw;
                  }

                  .Heziyun-onlineStatus {
                    font-size: 3vw;
                    gap: 1vw;
                  }
                }
              }
            }
          }

          .Heziyun-contact-content2 {
            width: 55vw;
            min-height: 35vw;
          }
        }
      }
    }

    .Heziyun-rightcontent2 {
      margin-top: 1vw;

      .Heziyun-contact-title {
        width: 6vw !important;
        height: 6vw !important;
      }
    }

    .Heziyun-rightcontent3 {
      margin-bottom: 2vw;

      .Heziyun-contact-title {
        width: 6vw !important;
        height: 6vw !important;
      }
    }

    .Heziyun-through {
      margin-bottom: 3vw;

      .Heziyun-contact-item {
        a {
          gap: 0.5rem;

          span {
            font-size: 3.5vw;
            padding: 3vw 1.5vw;
          }

          img {
            width: 12vw;
            height: 12vw;
            top: -12vw;
          }
        }
      }
    }
  }
}
</style>